import React from "react";
import Tooltip from "@mui/material/Tooltip";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { IgnoreZone } from "../../pages/Sites/SiteAboutPage/SiteAboutPageUtils/siteAboutInterfaces";
import { getIgnoreZoneLabelsAtPoint } from "./getIgnoreZoneLabelsAtPoint";

export type IgnoreZonePreviewHoverLayerProps = {
  zones: IgnoreZone[];
  enabled?: boolean;
  className?: string;
  children: React.ReactNode;
};

export const IgnoreZonePreviewHoverLayer = ({
  zones,
  enabled = true,
  className,
  children,
}: IgnoreZonePreviewHoverLayerProps) => {
  const { isFeatureEnabled } = useFeatureFlag();
  const showIgnoreZoneHover = isFeatureEnabled("showIgnoreZoneHover", false);
  const hoverEnabled = enabled && showIgnoreZoneHover;

  const containerRef = React.useRef<HTMLDivElement>(null);
  const [hoverLabels, setHoverLabels] = React.useState<string[]>([]);

  const handleMouseMove = React.useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!hoverEnabled || zones.length === 0) {
        setHoverLabels([]);
        return;
      }

      const container = containerRef.current;
      if (!container) {
        return;
      }

      const img = container.querySelector("img");
      if (!img) {
        setHoverLabels([]);
        return;
      }

      const rect = img.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0) {
        setHoverLabels([]);
        return;
      }

      const x = (event.clientX - rect.left) / rect.width;
      const y = (event.clientY - rect.top) / rect.height;

      if (x < 0 || x > 1 || y < 0 || y > 1) {
        setHoverLabels([]);
        return;
      }

      setHoverLabels(getIgnoreZoneLabelsAtPoint([x, y], zones));
    },
    [hoverEnabled, zones]
  );

  const handleMouseLeave = React.useCallback(() => {
    setHoverLabels([]);
  }, []);

  React.useEffect(() => {
    if (!hoverEnabled) {
      setHoverLabels([]);
    }
  }, [hoverEnabled]);

  const tooltipTitle =
    hoverLabels.length > 0 ? hoverLabels.join("\n") : "";

  return (
    <Tooltip
      open={hoverEnabled && hoverLabels.length > 0}
      followCursor
      title={tooltipTitle}
      placement="top"
      disableHoverListener
      disableFocusListener
      disableTouchListener
      componentsProps={{
        tooltip: {
          sx: { fontFamily: "Mulish", whiteSpace: "pre-line" },
        },
      }}
    >
      <div
        ref={containerRef}
        className={className}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        {children}
      </div>
    </Tooltip>
  );
};
